import localforage from "localforage";
import { base64ToBytes, bytesToBase64 } from "./base64";
import type { UserInfo } from "../models/Interfaces";
import type { SecureStorage, EncryptedPayload } from "./types";
import { NoEncryptionKeyError, EncryptionVersion, Platform, Key } from "./types";

const CryptoOpts = {
  // In 2023 OWASP recommends 600k iterations for SHA-256 keys.
  // Let's just add some buffer.
  PBKDF2_ITERATIONS: 1_000_000,
  SALT_LENGTH: 256,
  AES_GCM_LENGTH: 256,
  AES_KW_LENGTH: 256,
};

/**
 * WebEncryptedStorage is an API wrapper around localForage that ensures all
 * data is encrypted when written, and decrypted on read.
 */
export default class WebEncryptedStorage implements SecureStorage {
  store: LocalForage;
  cryptoStore: LocalForage;
  clientCryptoKey: CryptoKey | null;

  constructor(options?: LocalForageOptions) {
    const config = options || { name: "DocketAppV1" };
    if (config.name === "CryptoStoreV1") {
      throw new Error("Cannot create a store with the reserved name 'CryptoStoreV1'");
    }

    localforage.config(config);
    this.store = localforage.createInstance(config);
    this.cryptoStore = localforage.createInstance({ name: "CryptoStoreV1" });
    this.clientCryptoKey = null;
  }

  getEncryptionKey(): Promise<CryptoKey> {
    if (this.clientCryptoKey) {
      return Promise.resolve(this.clientCryptoKey);
    }
    return Promise.reject(new NoEncryptionKeyError());
  }

  generateIV(): Uint8Array {
    // https://developer.mozilla.org/en-US/docs/Web/API/AesGcmParams
    return window.crypto.getRandomValues(new Uint8Array(12));
  }

  serialize<T>(val: T): Promise<string> {
    return Promise.resolve(JSON.stringify(val));
  }

  // Note that `deserialize` does NOT completely work for classes.
  // There's a long reason for this that relates to type erasure in TS, but
  // the short version is that methods don't get hydrated back onto the object after parsing.
  // I thought JS was like "whatever it fits this shape here are some functions that work" but NOPE.
  deserialize<T>(val: string): Promise<T> {
    return Promise.resolve(JSON.parse(val) as T);
  }

  async encrypt(plaintext: string): Promise<EncryptedPayload> {
    const encoder = new TextEncoder();
    const buf = encoder.encode(plaintext);

    const key = await this.getEncryptionKey();
    const iv = this.generateIV();
    const ciphertext = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv: iv }, key, buf);
    return {
      payload: ciphertext,
      iv: iv,
      version: EncryptionVersion.AES_GCM_256,
      platform: Platform.Browser,
    };
  }

  async decrypt(ciphertext: ArrayBuffer, iv: Uint8Array): Promise<string> {
    const key = await this.getEncryptionKey();
    const plaintextBuf = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv },
      key,
      ciphertext,
    );

    const decoder = new TextDecoder();
    const plaintext = decoder.decode(plaintextBuf);
    return plaintext;
  }

  async getItem<T>(key: string): Promise<T | null> {
    const encryptedItem = await this.store.getItem<EncryptedPayload>(key);
    if (encryptedItem === null) {
      return null;
    }

    let decrypted: string;
    try {
      decrypted = await this.decrypt(encryptedItem.payload, encryptedItem.iv);
    } catch (err) {
      if (err instanceof NoEncryptionKeyError) {
        throw new NoEncryptionKeyError(`Could not getItem for key '${key}'.`);
      }
      throw err;
    }
    const deserialized = await this.deserialize<T>(decrypted);

    return deserialized;
  }

  async setItem<T>(key: string, value: T): Promise<T> {
    const serializedVal = await this.serialize(value);
    let encryptedVal: EncryptedPayload;
    try {
      encryptedVal = await this.encrypt(serializedVal);
    } catch (err) {
      if (err instanceof NoEncryptionKeyError) {
        throw new NoEncryptionKeyError(`Could not set key '${key}'.`);
      }
      throw err;
    }
    await this.store.setItem<EncryptedPayload>(key, encryptedVal);
    return value;
  }

  public async updateCryptoKey(u: UserInfo, password: string): Promise<void> {
    if (!u || !password || !u.client_key) {
      // Can't do much without both the password and client_key
      throw new Error("Attempted to update the crypto key without a user, client_key, or password");
    }

    // Generate a key for secret derivation
    const keyAsBytes = base64ToBytes(u.client_key);
    const importedHkdf = await window.crypto.subtle.importKey("raw", keyAsBytes, "HKDF", false, [
      "deriveBits",
      "deriveKey",
    ]);
    const hkdfSalt = window.crypto.getRandomValues(new Uint8Array(CryptoOpts.SALT_LENGTH));
    const hkdfParams = {
      name: "HKDF",
      hash: "SHA-256",
      salt: hkdfSalt,
      info: new TextEncoder().encode(`DocketPWAWebClient_${u.email || "unknown-email"}`),
    };
    const derivedKeyParams = { name: "AES-GCM", length: CryptoOpts.AES_GCM_LENGTH };
    // We need this key to be extractable since it is later wrapped.
    let derivedKey: CryptoKey;
    try {
      derivedKey = await window.crypto.subtle.deriveKey(
        hkdfParams,
        importedHkdf,
        derivedKeyParams,
        true,
        ["encrypt", "decrypt"],
      );
    } catch (e) {
      console.error(e);
      throw e;
    }

    const curItems = new Map<string, unknown>();
    if (this.clientCryptoKey) {
      // If we already have a key we need to decrypt everything in the database with the current key, then re-encrypt it once stored.
      // This happens when a user changes their password.
      // Don't remove/re-write the key or salt here
      const reservedKeys: string[] = [Key.EncryptionKey, Key.EncryptionSaltKey];
      const keys: string[] = (await this.keys()).filter((k) => !reservedKeys.includes(k));
      for (let i = 0; i < keys.length; i++) {
        // eslint-disable-next-line security/detect-object-injection
        const key = keys[i];
        curItems.set(key, await this.getItem(key));
      }
    } else {
      // No key yet - we need to build everything on the client_key
      // If we're creating a new crypto key we should clear the store to ensure all cached data is gone.
      await this.store.clear();
    }

    // The crypto key should NEVER BE WRITTEN directly to storage! We must first wrap the key with the password. See #wrapKey.
    this.clientCryptoKey = derivedKey;

    // Re-write everything to storage, re-encrypting it all
    const pendingSetPromises: Promise<unknown>[] = [];
    for (const [k, v] of curItems.entries()) {
      pendingSetPromises.push(this.setItem(k, v));
    }
    await Promise.all(pendingSetPromises);

    const wrappedSalt: Uint8Array = window.crypto.getRandomValues(
      new Uint8Array(CryptoOpts.SALT_LENGTH),
    );
    const wrappedKey = await this.wrapKey(this.clientCryptoKey, wrappedSalt, password);
    const b64Salt = bytesToBase64(wrappedSalt);
    const b64Key = bytesToBase64(wrappedKey);
    await this.cryptoStore.setItem(Key.EncryptionSaltKey, b64Salt);
    await this.cryptoStore.setItem(Key.EncryptionKey, b64Key);
  }

  private async wrapKey(
    clientCryptoKey: CryptoKey,
    salt: Uint8Array,
    password: string,
  ): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();
    const passwordKey: CryptoKey = await window.crypto.subtle.importKey(
      "raw",
      encoder.encode(password),
      { name: "PBKDF2" },
      false,
      ["deriveBits", "deriveKey"],
    );

    const wrappingKey: CryptoKey = await window.crypto.subtle.deriveKey(
      { name: "PBKDF2", salt, iterations: CryptoOpts.PBKDF2_ITERATIONS, hash: "SHA-256" },
      passwordKey,
      { name: "AES-KW", length: CryptoOpts.AES_KW_LENGTH },
      true,
      ["wrapKey", "unwrapKey"],
    );
    return await window.crypto.subtle.wrapKey("raw", clientCryptoKey, wrappingKey, {
      name: "AES-KW",
    });
  }

  private async unwrapKey(
    wrappedKey: Uint8Array<ArrayBuffer>,
    salt: Uint8Array<ArrayBuffer>,
    password: string,
  ): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const passwordKey: CryptoKey = await window.crypto.subtle.importKey(
      "raw",
      encoder.encode(password),
      { name: "PBKDF2" },
      false,
      ["deriveBits", "deriveKey"],
    );

    const wrappingKey: CryptoKey = await window.crypto.subtle.deriveKey(
      { name: "PBKDF2", salt, iterations: CryptoOpts.PBKDF2_ITERATIONS, hash: "SHA-256" },
      passwordKey,
      { name: "AES-KW", length: CryptoOpts.AES_KW_LENGTH },
      true,
      ["wrapKey", "unwrapKey"],
    );

    return await window.crypto.subtle.unwrapKey(
      "raw",
      wrappedKey,
      wrappingKey,
      { name: "AES-KW" },
      { name: "AES-GCM", length: CryptoOpts.AES_GCM_LENGTH },
      true,
      ["encrypt", "decrypt"],
    );
  }

  /**
   * Check if the database is an encrypted, locked state where data cannot be read.
   *
   * @returns true if locked, false otherwise
   */
  locked(): boolean {
    return !this.clientCryptoKey;
  }

  /**
   * Unlock the encrypted database using the user's password.
   *
   * @param password The password for the user's account.
   * @returns True if unlocked, false otherwise.
   * @throws Error - Cipher job failed if it could not be unlocked.
   */
  async unlock(password: string): Promise<boolean> {
    if (this.clientCryptoKey) return true;

    const b64WrappedSaltBytes: string | null = await this.cryptoStore.getItem<string>(
      Key.EncryptionSaltKey,
    );

    const b64WrappedKeyBytes: string | null = await this.cryptoStore.getItem<string>(
      Key.EncryptionKey,
    );

    if (!b64WrappedKeyBytes || !b64WrappedSaltBytes) {
      return false;
    }

    const saltBytes = base64ToBytes(b64WrappedSaltBytes);
    const keyBytes = base64ToBytes(b64WrappedKeyBytes);

    const unwrappedKey = await this.unwrapKey(keyBytes, saltBytes, password);
    this.clientCryptoKey = unwrappedKey;
    return true;
  }

  removeItem(key: string): Promise<void> {
    return this.store.removeItem(key);
  }

  /**
   * Remove everything, and forget the key (in memory and wrapped in the crypto store): until a new
   * user's key is made, nothing more can be written, and nothing can be re-encrypted for them.
   */
  async clear(): Promise<void> {
    // First, so a write that comes in meanwhile fails instead of landing after the data is gone.
    this.clientCryptoKey = null;
    await this.store.clear();
    await this.cryptoStore.removeItem(Key.EncryptionKey);
    await this.cryptoStore.removeItem(Key.EncryptionSaltKey);
  }

  length(): Promise<number> {
    return this.store.length();
  }

  key(keyIndex: number): Promise<string> {
    return this.store.key(keyIndex);
  }

  keys(): Promise<string[]> {
    return this.store.keys();
  }
}
